import React, { useContext, useCallback } from 'react';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

import { OvzFacility } from '../../models';
import { EvidenceAPI } from '../../enums';

import { fetchOvzFacility, useLandPlotTypes } from './ovz-facilities-api';
import { DetailContext } from '@eas/common-web';
import {
  MapEvidenceContext,
  MapEvidenceView,
} from '../../components/evidence/map-evidence/map-context';

import { useMarkerPopupFactory } from '../../components/map/hook/marker-popup-factory';
import {
  CrzpMarker,
  MarkerType,
  MarkerProps,
} from '../../components/map/map-types';
import { MapContainer } from '../../components/map/map-container';
import { RemoteMarkers } from '../../components/map/remote-markers';
import {
  createMarkerIcon,
  createLeafletMarker,
} from '../../components/map/marker';

export function FacilityMarkerTitle({ name }: { name: string }) {
  return (
    <>
      <Typography variant="body2">
        Název provozovny: <b>{name}</b>
      </Typography>
    </>
  );
}

export function FacilityMarkerBody({
  data,
  setActive,
}: {
  data: OvzFacility & { landPlotTypeLabel: string };
  setActive: (id: string) => void;
}) {
  return (
    <>
      <Typography variant="body2">
        IČP: <b>{data?.facilityId}</b>
      </Typography>
      <br />
      <Typography variant="body2">
        Typ parcely: <b>{data?.landPlotTypeLabel}</b>
      </Typography>
      <Typography variant="body2">
        Číslo parcely: <b>{data?.primaryLandPlotNumber ?? '-'}</b>
      </Typography>
      <Typography variant="body2">
        KÚ:&nbsp;
        <b>
          {data.cadastralTerritory
            ? `${data.cadastralTerritory.isuiCode} - ${data.cadastralTerritory.name}`
            : '-'}
        </b>
      </Typography>
      <Typography variant="body2">
        Adresní místo: <b>{data?.address?.addressPlaceRuian?.label}</b>
      </Typography>
      <Typography component="div" variant="body2">
        <>
          <p>
            Zeměpisná šířka: <b>{data?.wgs84Coordinates?.lat}</b>
          </p>
          <p>
            Zeměpisná délka: <b>{data?.wgs84Coordinates?.lon}</b>
          </p>
        </>
      </Typography>
      <br />
      <Button
        size="small"
        variant="contained"
        onClick={() => setActive(data.id)}
      >
        Spravovat
      </Button>
      <br />
      <br />
    </>
  );
}

export const OvzFacilitiesMarkerPopupBody = ({
  data,
  setActive,
}: {
  data: OvzFacility & { landPlotTypeLabel: string };
  marker: CrzpMarker;
  setActive: (id: string) => void;
}): React.ReactElement => {
  return (
    <div
      style={{
        backgroundColor: 'white',
        color: 'black',
      }}
    >
      <FacilityMarkerTitle name={data?.name ?? ''} />
      <FacilityMarkerBody
        data={data!}
        setActive={() => {
          setActive(data.id);
        }}
      />
    </div>
  );
};

const inactiveIcon = createMarkerIcon(MarkerType.FILLED_GREY, 'small');
const activeIcon = createMarkerIcon(MarkerType.FILLED_BLUE, 'small');
const selectedIconActive = createMarkerIcon(MarkerType.BIG_RED, 'medium');
const selectedIconInactive = createMarkerIcon(MarkerType.BIG_GREY, 'medium');

export function OvzFacilitiesMap() {
  const { setView } = useContext(MapEvidenceContext);
  const { setActive, onPersisted, source } = useContext(DetailContext);

  const landPlotTypes = useLandPlotTypes();

  const activateFacility = (id: string) => {
    setActive(id);
    onPersisted(id);
    setView(MapEvidenceView.DETAIL);
  };

  const config = useMarkerPopupFactory<OvzFacility>({
    fetchData: (marker) => fetchOvzFacility(marker.id ?? '')?.json(),
    PopupBodyComponent: (props) => {
      const landPlotTypeLabel =
        landPlotTypes.items.find((i) => i.id === props.data?.landPlotType)
          ?.name ?? '-';
      return OvzFacilitiesMarkerPopupBody({
        ...props,
        setActive: activateFacility,
        data: { ...props.data, landPlotTypeLabel },
      });
    },
  });

  const customCreateLeafletMarker = useCallback(
    (props: MarkerProps) => {
      let customIcon: any = {};
      const markerLeafletOptions = {} as L.MarkerOptions;
      if (source.data?.id === props.id) {
        customIcon = props.active ? selectedIconActive : selectedIconInactive;
        markerLeafletOptions.title = props.active ? 'Aktivní' : 'Neaktivní';
      } else if (props.active) {
        customIcon = activeIcon;
        markerLeafletOptions.title = 'Aktivní';
      } else {
        customIcon = inactiveIcon;
        markerLeafletOptions.title = 'Neaktivní';
      }

      return createLeafletMarker({
        ...props,
        customIcon,
        markerLeafletOptions,
      });
    },
    [source?.data?.id]
  );

  return (
    <MapContainer showSuggest>
      <RemoteMarkers
        api={EvidenceAPI.OVZ_FACILITIES}
        markerPopupConfig={config}
        createLeafletMarker={customCreateLeafletMarker}
      />
    </MapContainer>
  );
}
